 import React from 'react';
import './Bird.css';

import heroBird from "../../assets/heroBird.jpg"; 
import raptorImg from "../../assets/raptorImg.jpg";
import tropicalImg from "../../assets/tropicalImg.jpg";
import waterfowlImg from "../../assets/waterfowlImg.jpg";
import songbirdImg from "../../assets/songbirdImg.jpg";
import impactImg from "../../assets/ImpactImg.jpg";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

export default function BirdsLandingPage() {

  const birdFamilies = [
    { 
      id: 'raptors', 
      name: 'Raptors: Eagles, Hawks, Owls & Falcons', 
      img: raptorImg,
      detail: `Raptors are apex predators and provide critical ecosystem services in Pakistan. According to WWF Pakistan, a single pair of Barn Owls can consume over 3,000 rodents per year, saving farmers an estimated $200 per acre in pesticide costs. Vultures prevent disease outbreaks by consuming carcasses. However, the population of raptors in Pakistan has declined by over 60% in the last 20 years. The primary causes are habitat loss due to deforestation, secondary poisoning from rodenticides, and electrocution on power lines. The loss of raptors has led to an increase in rodent populations, resulting in significant crop damage and increased use of chemical pesticides. Conservation efforts must include planting native trees for nesting, banning toxic pesticides like carbofuran, and installing artificial nesting platforms in rural areas.`
    },
    { 
      id: 'tropical', 
      name: 'Forest Birds: Parrots, Sunbirds & Kingfishers', 
      img: tropicalImg,
      detail: `The forests of Northern Pakistan, including KPK, Kashmir, and Murree, are home to critical pollinators and seed dispersers. Species such as the Himalayan Monal and Rose-ringed Parakeet play a vital role in maintaining forest health. Sunbirds are key pollinators, while parrots disperse seeds over long distances. The IUCN reports that approximately 40% of forest bird species in the region are now threatened. The main threats are deforestation for timber and agriculture, illegal trapping for the pet trade, and the impacts of climate change on flowering and fruiting cycles. The disappearance of these birds leads to reduced forest regeneration, increased soil erosion, and a decline in ecotourism revenue. Solutions include establishing protected bird sanctuaries, enforcing anti-poaching laws with heavy fines, and community-based conservation programs.`
    },
    { 
      id: 'waterfowl', 
      name: 'Waterfowl: Ducks, Cranes, Herons & Flamingos', 
      img: waterfowlImg,
      detail: `Pakistan lies on the Indus Flyway and hosts over 1.2 million migratory waterfowl each winter, according to the Pakistan Wetlands Programme. Key sites include Haleji Lake, Tarbela Dam, and the Indus River. These birds control pests in agricultural fields and serve as indicators of water quality. Pakistan has lost an estimated 70% of its natural wetlands due to urbanization, industrial pollution, and the construction of dams and barrages. This loss contributed to the severity of the 2022 floods and has impacted the livelihoods of over 2 million fishermen. The Siberian Crane is now considered locally extinct in Pakistan. To reverse this, Pakistan must restore key wetlands under the Ramsar Convention, enforce a seasonal ban on hunting from October to February, and mandate wastewater treatment for all industries.`
    },
    { 
      id: 'songbirds', 
      name: 'Songbirds: Sparrows, Bulbuls & Mynas', 
      img: songbirdImg,
      detail: `Common songbirds like the House Sparrow and Red-vented Bulbul are essential to urban ecosystems. Research from Karachi University indicates that a single pair of sparrows feeds approximately 6,000 insects to their young during the breeding season, providing natural pest control. They are also crucial for seed dispersal in cities. However, urbanization has caused an 80% decline in House Sparrow populations in major Pakistani cities over the past 15 years. Causes include loss of green spaces, air and noise pollution, and the widespread use of insecticides. The decline correlates with an increase in mosquito-borne diseases like dengue. Reversing this trend requires simple actions: providing water and food on rooftops, planting native trees in urban areas, reducing pesticide use, and installing nest boxes in public parks.`
    }
  ];

  const causes = [
    { title: 'Habitat Loss', desc: 'Deforestation and conversion of wetlands for housing and agriculture have destroyed nesting and feeding grounds for millions of birds.' },
    { title: 'Pollution & Poisoning', desc: 'Pesticides, industrial waste, and plastic pollution directly kill birds and contaminate their food sources.' },
    { title: 'Climate Change', desc: 'Changing weather patterns disrupt migration timing and food availability, making survival difficult for many species.' },
    { title: 'Illegal Hunting & Trade', desc: 'Birds are illegally trapped for the pet trade and hunted during migration, despite existing wildlife laws.' }
  ];

  const effects = [
    { title: 'Economic Damage', desc: 'Loss of pest control leads to over $2 billion in annual crop damage, increasing food prices for consumers.' },
    { title: 'Environmental Instability', desc: 'Without forests and wetlands, Pakistan faces more frequent floods, landslides, and droughts.' },
    { title: 'Public Health Risks', desc: 'The decline in insect-eating birds has led to a rise in mosquitoes and an increase in dengue and malaria cases.' },
    { title: 'Biodiversity Collapse', desc: 'The loss of birds disrupts the entire food chain, leading to the decline of other wildlife and plant species.' }
  ];

  const solutions = [
    { title: 'Habitat Restoration', desc: 'Launch a national campaign to plant 10 million native trees and restore 10 critical wetlands by 2030.' },
    { title: 'Stronger Laws & Enforcement', desc: 'Ban toxic pesticides, enforce a complete ban on migratory bird hunting, and increase penalties for wildlife trafficking.' },
    { title: 'Community Involvement', desc: 'Promote eco-tourism and provide incentives for local communities to protect birds and their habitats.' },
    { title: 'Public Awareness', desc: 'Educate citizens on simple actions like providing water, reducing chemical use, and reporting illegal activities.' }
  ];

  return (
    <div className="birds-site">
      <Navbar />

      <section className="hero-clean">
        <img src={heroBird} className="hero-image" alt="Birds of Pakistan"/>
      </section>

      <section className="intro-section">
        <div className="container">
          <h1>Pakistan's Avian Crisis</h1>
          <p>Pakistan is home to 668 bird species. Over the last two decades, we have lost 50% of our common bird populations. This website presents the research, causes, and solutions.</p>
        </div>
      </section>

      <section className="families-section" id="families">
        <div className="container">
          <h2 className="section-title">The 4 Key Bird Groups of Pakistan</h2>
          <div className="cards-grid">
            {birdFamilies.map((bird) => (
              <div key={bird.id} className="bird-card">
                <img src={bird.img} alt={bird.name} />
                <div className="card-content">
                  <h3>{bird.name}</h3>
                  <p className="card-detail">{bird.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="causes-section">
        <div className="container">
          <h2 className="section-title">Root Causes of Decline</h2>
          <div className="text-grid">
            {causes.map((c) => (
              <div className="text-card" key={c.title}>
                <h4>{c.title}</h4>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="impact-section">
        <img src={impactImg} className="impact-bg" alt="impact"/>
        <div className="impact-overlay"></div>
        <div className="container impact-content">
          <h2 className="section-title white">Consequences If No Action Is Taken</h2>
          <div className="text-grid">
            {effects.map((e) => (
              <div className="text-card glass" key={e.title}>
                <h4>{e.title}</h4>
                <p>{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="solutions-section">
        <div className="container">
          <h2 className="section-title">Recommended Conservation Actions</h2>
          <div className="text-grid">
            {solutions.map((s) => (
              <div className="text-card" key={s.title}>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="stories-section">
        <div className="container">
          <h2 className="section-title">Conservation in Action: Case Studies</h2>
          <div className="story-grid">
            <div className="story-card">
              <h3>Vulture Safe Zones in Sindh</h3>
              <p>Following a 99% decline due to the veterinary drug Diclofenac, the Government of Pakistan banned its use. WWF established Vulture Safe Zones, and populations are now showing signs of recovery. This proves policy change can save species.</p>
            </div>
            <div className="story-card">
              <h3>Community-Based Conservation in Gilgit-Baltistan</h3>
              <p>Local communities in the north now manage bird watching tourism. By protecting species like the Himalayan Monal, they generate sustainable income, reducing reliance on hunting and logging.</p>
            </div>
            <div className="story-card">
              <h3>Urban Nest Box Program in Islamabad</h3>
              <p>The Capital Development Authority installed 5,000 nest boxes in parks. Within 18 months, monitoring showed a 25% increase in breeding pairs of native songbirds in the area.</p>
            </div>
          </div>
          <div className="final-cta">
            <h3>What You Can Do Today</h3>
            <p>Place a water source on your roof. Plant one native tree. Avoid buying caged wild birds. Report illegal hunting. Share this information. Individual action leads to collective impact.</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}